import axios, { AxiosInstance } from "axios";

export type SentryHttpContext = {
  status: number | undefined;
  method: string | undefined;
  url: string | undefined;
  baseUrl: string | undefined;
  params: unknown;
  responseData: unknown;
  requestData: unknown;
};

// Endpoints whose request body is safe to send to Sentry (no sensitive data).
const ENDPOINTS_WITH_REQUEST_BODY = ["/broker/document/sign"];

function parseRequestData(data: unknown): unknown {
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data;
}

export function attachSentryResponseInterceptor(instance: AxiosInstance): void {
  instance.interceptors.response.use(null, (error) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error);

    const res = error.response;
    const config = error.config;

    const captureRequestBody = ENDPOINTS_WITH_REQUEST_BODY.some((endpoint) =>
      config?.url?.includes(endpoint),
    );

    const httpContext: SentryHttpContext = {
      status: res?.status,
      method: config?.method?.toUpperCase(),
      url: config?.url,
      baseUrl: config?.baseURL,
      params: config?.params,
      responseData:
        res?.data != null && !(res.data instanceof Blob) ? res.data : undefined,
      requestData: captureRequestBody
        ? parseRequestData(config?.data)
        : undefined,
    };

    Object.assign(error, { _sentryHttpContext: httpContext });

    return Promise.reject(error);
  });
}

export function getSentryHttpContext(
  error: unknown,
): SentryHttpContext | undefined {
  return (error as { _sentryHttpContext?: SentryHttpContext } | undefined)
    ?._sentryHttpContext;
}

// Builds the Sentry "extra" object from the HTTP context attached by the
// interceptor. Reusable wherever an error is captured manually (no react-query).
export function sentryHttpExtras(error: unknown): Record<string, unknown> {
  const httpCtx = getSentryHttpContext(error);
  if (!httpCtx) return {};

  const extras: Record<string, unknown> = {
    "http.status_code": httpCtx.status,
    "http.method": httpCtx.method,
    url: httpCtx.url,
    base_url: httpCtx.baseUrl,
    params: httpCtx.params,
  };
  if (httpCtx.responseData !== undefined) {
    extras.response_data = httpCtx.responseData;
  }
  if (httpCtx.requestData !== undefined) {
    extras.request_data = httpCtx.requestData;
  }
  return extras;
}
